import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Collapse, IconButton, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { highlightSql } from '../utilities/sqlHighlight';

const BRAND = '#04aeea';

/* ── Entry kinds → icon + accessible label ── */
const KIND_META = {
    status:   { Icon: AutoAwesomeOutlinedIcon,  noun: 'Step'      },
    tool:     { Icon: HandymanOutlinedIcon,     noun: 'Tool'      },
    sql:      { Icon: StorageOutlinedIcon,      noun: 'SQL'       },
    thinking: { Icon: PsychologyOutlinedIcon,   noun: 'Reasoning' },
    cohort:   { Icon: GroupsOutlinedIcon,       noun: 'Cohort'    },
};

const shimmerSx = (duration = 2) => ({
    background: `linear-gradient(90deg, #94a3b8 0%, ${BRAND} 50%, #94a3b8 100%)`,
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    '@keyframes activityShimmer': {
        '0%': { backgroundPosition: '200% center' },
        '100%': { backgroundPosition: '-200% center' },
    },
    animation: `activityShimmer ${duration}s linear infinite`,
});

const formatDuration = (ms) => {
    if (!ms || ms < 0) return '';
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

/* ── Per-entry state marker: spinner while active, dot when settled ── */
const StateMarker = ({ state }) => {
    if (state === 'active') {
        return (
            <Box sx={{
                width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
                border: `1.5px solid ${BRAND}`, borderTopColor: 'transparent',
                '@keyframes activitySpin': { to: { transform: 'rotate(360deg)' } },
                animation: 'activitySpin 0.75s linear infinite',
            }} />
        );
    }
    if (state === 'error') {
        return <ErrorOutlineIcon sx={{ fontSize: 12, color: 'error.main', flexShrink: 0 }} />;
    }
    return (
        <Box sx={{
            width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
        }}>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#60a5fa' }} />
        </Box>
    );
};

/* ── One row in the timeline ── */
const ActivityRow = ({ entry, isLast }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const { Icon, noun } = KIND_META[entry.kind] || KIND_META.status;
    const hasDetail = Boolean(entry.detail && String(entry.detail).trim());
    const isActive = entry.state === 'active';
    const isSql = entry.kind === 'sql';

    // SQL streams in token by token while the agent works, so memoise on the
    // text itself rather than re-tokenising on every parent render.
    const detailBody = useMemo(
        () => (isSql ? highlightSql(entry.detail, theme.palette.mode) : entry.detail),
        [isSql, entry.detail, theme.palette.mode]
    );

    return (
        <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
            {/* Rail: marker with a connecting line down to the next row */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, pt: 0.45 }}>
                <StateMarker state={entry.state} />
                {!isLast && <Box sx={{ width: '1px', flexGrow: 1, bgcolor: 'divider', my: 0.35, minHeight: 8 }} />}
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0, pb: isLast ? 0 : 0.85 }}>
                <Box
                    onClick={hasDetail ? () => setOpen(o => !o) : undefined}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.65,
                        cursor: hasDetail ? 'pointer' : 'default',
                        borderRadius: '6px',
                        mx: -0.5, px: 0.5, py: 0.15,
                        '&:hover': hasDetail ? { bgcolor: 'rgba(4,174,234,0.06)' } : undefined,
                        transition: 'background-color 0.15s ease',
                    }}
                >
                    <Icon sx={{ fontSize: 13, color: isActive ? BRAND : 'text.secondary', flexShrink: 0 }} />
                    <Typography
                        component="span"
                        title={entry.label}
                        sx={{
                            fontSize: '0.755rem',
                            lineHeight: 1.45,
                            fontWeight: isActive ? 600 : 500,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...(isActive ? shimmerSx(1.9) : { color: 'text.secondary' }),
                        }}
                    >
                        {entry.label}
                    </Typography>
                    {hasDetail && (
                        <KeyboardArrowDownIcon sx={{
                            fontSize: 14, color: 'text.disabled', flexShrink: 0, ml: 'auto',
                            transform: open ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.18s ease',
                        }} />
                    )}
                </Box>

                {hasDetail && (
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            component="pre"
                            aria-label={`${noun} detail`}
                            sx={{
                                m: 0, mt: 0.5,
                                px: 1, py: 0.75,
                                bgcolor: 'surface.sunken',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '6px',
                                fontSize: '0.67rem',
                                fontFamily: entry.kind === 'thinking'
                                    ? 'inherit'
                                    : '"Fira Code", "Cascadia Code", Consolas, monospace',
                                lineHeight: 1.55,
                                color: 'text.secondary',
                                // Highlighted SQL keeps its own indentation; wrapping
                                // mid-token would break the visual structure.
                                whiteSpace: 'pre-wrap',
                                wordBreak: isSql ? 'normal' : 'break-word',
                                overflowX: isSql ? 'auto' : 'hidden',
                                maxHeight: 170,
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: 4, height: 4 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(4,174,234,0.25)', borderRadius: 4 },
                            }}
                        >
                            {detailBody}
                        </Box>
                    </Collapse>
                )}
            </Box>
        </Box>
    );
};

/**
 * Collapsible agent activity timeline.
 *
 * While the agent is running the panel is expanded and streams each step live.
 * Once the run settles it collapses to a single "Thought for 12s" summary row
 * that can be reopened to review everything the agent did.
 *
 * @param {Array} entries   - [{ id, kind, label, detail, state }]
 * @param {boolean} running - true while the agent is still working
 * @param {number} startedAt - Date.now() when the run began (drives the live timer)
 * @param {number} durationMs - final elapsed time; used once running is false
 * @param {boolean} stopped - true when the user cancelled the run
 */
const AgentActivity = ({ entries = [], running = false, startedAt, durationMs, stopped = false }) => {
    // While running the panel is open and follows the agent; once it settles it
    // collapses on its own. `userToggled` stops the auto-collapse from fighting a
    // user who deliberately opened or closed it.
    const [expanded, setExpanded] = useState(running);
    const [userToggled, setUserToggled] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (userToggled) return;
        setExpanded(running);
    }, [running, userToggled]);

    // Live elapsed timer — only ticks while the run is in flight.
    useEffect(() => {
        if (!running || !startedAt) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [running, startedAt]);

    const elapsedLabel = useMemo(() => {
        if (running) return startedAt ? formatDuration(now - startedAt) : '';
        return formatDuration(durationMs);
    }, [running, startedAt, now, durationMs]);

    if (entries.length === 0 && !running) return null;

    const headerLabel = stopped
        ? (running ? 'Stopping…' : 'Stopped')
        : running
            ? 'Working'
            : elapsedLabel
                ? `Thought for ${elapsedLabel}`
                : 'Thought process';

    const toggle = () => {
        setUserToggled(true);
        setExpanded(e => !e);
    };

    return (
        <Box sx={{
            border: '1px solid',
            borderColor: running ? 'rgba(4,174,234,0.35)' : 'divider',
            borderRadius: '10px',
            bgcolor: running ? 'rgba(4,174,234,0.05)' : 'surface.sunken',
            overflow: 'hidden',
            transition: 'border-color 0.3s ease, background-color 0.3s ease',
        }}>
            {/* ── Summary header ── */}
            <Box
                onClick={toggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                }}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1.1, py: 0.7,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'rgba(4,174,234,0.06)' },
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '-2px' },
                    transition: 'background-color 0.15s ease',
                }}
            >
                <PsychologyOutlinedIcon sx={{ fontSize: 15, color: running ? BRAND : 'text.secondary', flexShrink: 0 }} />
                <Typography
                    component="span"
                    sx={{
                        fontSize: '0.755rem',
                        fontWeight: 500,
                        ...(running ? shimmerSx(2) : { color: 'text.secondary' }),
                    }}
                >
                    {headerLabel}
                </Typography>
                {running && elapsedLabel && (
                    <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {elapsedLabel}
                    </Typography>
                )}
                {!running && entries.length > 0 && (
                    <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                        · {entries.length} {entries.length === 1 ? 'step' : 'steps'}
                    </Typography>
                )}
                <IconButton
                    size="small"
                    tabIndex={-1}
                    aria-hidden
                    sx={{
                        ml: 'auto', p: 0.25, color: 'text.disabled',
                        transform: expanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                    }}
                >
                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* ── Timeline ── */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 1.1, pb: 1, pt: 0.25 }}>
                    {entries.length === 0 ? (
                        <Typography sx={{ fontSize: '0.72rem', ...shimmerSx(1.9) }}>
                            Starting up…
                        </Typography>
                    ) : (
                        entries.map((entry, i) => (
                            <ActivityRow key={entry.id ?? i} entry={entry} isLast={i === entries.length - 1} />
                        ))
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default AgentActivity;
